import Badge from '@amzn/awsui-components-core/badge';
import { faExternalLinkAlt, faStar as solidStar } from '@fortawesome/free-solid-svg-icons';
import { faStar as regularStar } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlobalSearchBar as SharedGlobalSearchBar } from '@amzn/das-finsuite-shared-ui';
import type { SearchResult as SharedSearchResult, SearchCategory } from '@amzn/das-finsuite-shared-ui';
import { useProgramSearch, SearchResult } from '../../hooks/useProgramSearch';
import { getProgramStatusBadge } from '../../utils/programStatusUtils';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { selectFavoritesSet, selectHasAttemptedFavoritesFetch } from '../../store/selectors/userPreferencesSelectors';
import { toggleUserFavorite, optimisticToggleFavorite } from '../../store/slices/userPreferencesSlice';
import { FavoriteAction } from '../../types/userPreferences/userPreferences.types';
import { getFavoriteKey, getFavoriteTypeAndId } from '../../utils/favoriteUtils';
import { logger } from '../../utils/logger';

type CategoryId = 'all' | 'programs' | 'asins' | 'partNumbers' | 'reports' | 'announcements' | 'resources';

const CATEGORIES: SearchCategory[] = [
  { id: 'all', label: 'All' },
  { id: 'programs', label: 'Programs' },
  { id: 'asins', label: 'ASINs' },
  { id: 'partNumbers', label: 'Part Numbers' },
  { id: 'reports', label: 'Reports' },
  { id: 'announcements', label: 'Announcements' },
  { id: 'resources', label: 'Resources' },
];

const SECTION_CATEGORY_MAP: Record<string, CategoryId> = {
  Programs: 'programs',
  ASINs: 'asins',
  'Part Numbers': 'partNumbers',
  Reports: 'reports',
  Announcements: 'announcements',
  Resources: 'resources',
};

const TYPE_BADGE: Partial<Record<SearchResult['type'], string>> = {
  'product-line': 'PL',
  rpn: 'RPN',
  fpn: 'FPN',
};

const BADGE_CLASS: Partial<Record<SearchResult['type'], string>> = {
  'product-line': 'global-search__result-badge--product-line',
  rpn: 'global-search__result-badge--rpn',
  fpn: 'global-search__result-badge--fpn',
};

const GlobalSearchBar: React.FC = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<CategoryId>('all');

  const { sections, ensureDataLoaded, isSearching } = useProgramSearch(query);

  const dispatch = useAppDispatch();
  const favoriteIdsSet = useAppSelector(selectFavoritesSet);
  const hasAttemptedFavoritesFetch = useAppSelector(selectHasAttemptedFavoritesFetch);

  // Map Verity sections → SharedUI sections (add required `id` field)
  const mappedSections = useMemo(() => {
    const filtered =
      activeCategory === 'all' ? sections : sections.filter((s) => SECTION_CATEGORY_MAP[s.title] === activeCategory);
    return filtered.map((s) => ({
      ...s,
      results: s.results.map(
        (r, i) => ({ ...r, id: `${s.title}-${r.type}-${i}-${r.displayId || r.label || ''}` }) as SharedSearchResult
      ),
    }));
  }, [sections, activeCategory]);

  const handleResultClick = useCallback(
    (result: SharedSearchResult) => {
      if (result.path) {
        navigate(result.path);
      } else if (result.externalUrl) {
        window.open(result.externalUrl, '_blank', 'noopener,noreferrer');
      }
    },
    [navigate]
  );

  const handleToggleFavorite = useCallback(
    async (e: React.MouseEvent, result: SharedSearchResult) => {
      e.preventDefault();
      e.stopPropagation();
      const favoriteNode = result.favoriteNode as SearchResult['favoriteNode'];
      if (!favoriteNode || !hasAttemptedFavoritesFetch) return;

      const typeAndId = getFavoriteTypeAndId(favoriteNode);
      if (!typeAndId) return;

      const key = getFavoriteKey(favoriteNode);
      const isFav = key ? favoriteIdsSet.has(key) : false;
      const action = isFav ? FavoriteAction.REMOVED : FavoriteAction.ADDED;

      dispatch(
        optimisticToggleFavorite({
          favorite_type: typeAndId.favoriteType,
          item_id: typeAndId.itemId,
          isFavorited: isFav,
          action,
        })
      );

      try {
        await dispatch(
          toggleUserFavorite({ favorite_type: typeAndId.favoriteType, item_id: typeAndId.itemId, isFavorited: isFav })
        ).unwrap();
      } catch (error) {
        logger.error('GlobalSearchBar: Favorite toggle failed, reverting', { error });
        dispatch(
          optimisticToggleFavorite({
            favorite_type: typeAndId.favoriteType,
            item_id: typeAndId.itemId,
            isFavorited: !isFav,
            action: isFav ? FavoriteAction.ADDED : FavoriteAction.REMOVED,
          })
        );
      }
    },
    [dispatch, favoriteIdsSet, hasAttemptedFavoritesFetch]
  );

  const renderResultBadge = useCallback((result: SharedSearchResult) => {
    const badge = TYPE_BADGE[result.type as SearchResult['type']];
    if (!badge) return null;
    return (
      <span className={`global-search__result-badge ${BADGE_CLASS[result.type as SearchResult['type']] || ''}`}>
        {badge}
      </span>
    );
  }, []);

  const renderResultActions = useCallback(
    (result: SharedSearchResult) => {
      const favoriteNode = result.favoriteNode as SearchResult['favoriteNode'];
      const key = favoriteNode ? getFavoriteKey(favoriteNode) : null;
      const isFav = key ? favoriteIdsSet.has(key) : false;

      return (
        <>
          {result.type === 'rpn' && result.programStatus && getProgramStatusBadge(result.programStatus as string)}
          {result.type === 'rpn' && result.isConfidential && <Badge color="grey">Confidential</Badge>}
          {result.externalUrl && (
            <span
              className="global-search__external-link-btn"
              onClick={(e) => {
                e.stopPropagation();
                window.open(result.externalUrl!, '_blank', 'noopener,noreferrer');
              }}
              role="link"
              aria-label="Open external link"
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} />
            </span>
          )}
          {favoriteNode && (
            <span
              className={`global-search__favorite-btn ${isFav ? 'global-search__favorite-btn--active' : ''}`}
              onClick={(e) => handleToggleFavorite(e, result)}
              role="button"
              aria-label={isFav ? 'Remove from favorites' : 'Add to favorites'}
            >
              <FontAwesomeIcon icon={isFav ? solidStar : regularStar} />
            </span>
          )}
        </>
      );
    },
    [favoriteIdsSet, handleToggleFavorite]
  );

  return (
    <SharedGlobalSearchBar
      variant="verity"
      placeholder="Search Verity  ·  programs, reports, resources..."
      sections={mappedSections}
      onQueryChange={(q) => {
        setQuery(q);
        setActiveCategory('all');
      }}
      onResultClick={handleResultClick}
      onEnsureDataLoaded={ensureDataLoaded}
      minQueryLength={2}
      categories={CATEGORIES}
      activeCategory={activeCategory}
      onCategoryChange={(id) => setActiveCategory(id as CategoryId)}
      renderResultBadge={renderResultBadge}
      renderResultActions={renderResultActions}
      highlightQuery
      isLoading={isSearching}
    />
  );
};

export default GlobalSearchBar;
